/**
 * Quiz Player
 * Player component for Quiz content type
 */

import * as React from 'react';
import { Card, CardContent, cn, Progress } from '@ai-content-hub/content-kit';
import { QuestionCounterLabel } from '@ai-content-hub/content-core';
import {
    MCQPlayer,
    TextPlayer,
    ImagePlayer,
    RichTextPlayer,
    type MCQElementData,
    type MCQAnswer,
    type TextElementData,
    type ImageElementData,
    type RichTextElementData,
} from '@ai-content-hub/content-elements';
import { useLocale, ContentPlayerActionsContext } from '@ai-content-hub/content-core';
import type {
    QuizData,
    QuizItem,
    QuizConfig,
    QuizLayoutConfig,
    QuizTimerConfig,
} from './schema';
import { Clock } from 'lucide-react';
import type { QuizItemResult } from './scorer';

export interface QuizPlayerProps {
    data: QuizData;
    config?: QuizConfig;
    onAnswer?: (answer: Record<string, unknown>) => void;
    answer?: Record<string, unknown>;
    result?: {
        isCorrect?: boolean | null;
        score?: number;
        max?: number;
        details?: Record<string, unknown>;
    };
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
}

export type { QuizItemResult } from './scorer';

export interface QuizResults {
    items: QuizItemResult[];
    totalScore: number;
    max: number;
    percentage: number;
    passed: boolean;
    timeSpent: number;
}

export const QuizPlayer: React.FC<QuizPlayerProps> = ({
    data,
    config,
    onAnswer,
    answer: answerProp,
    result: resultProp,
    showFeedback: showFeedbackProp,
    isReview = false,
    disabled = false,
    className,
}) => {
    const layoutConfig = config?.layout as QuizLayoutConfig | undefined;
    const timerConfig = config?.timer as QuizTimerConfig | undefined;
    const i18nConfig = config?.i18n;
    const playerActions = React.useContext(ContentPlayerActionsContext);

    const { t } = useLocale({ config: i18nConfig });

    const [answers, setAnswers] = React.useState<Record<string, unknown>>(() =>
        answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)
            ? { ...answerProp }
            : {}
    );
    const [timeRemaining, setTimeRemaining] = React.useState(
        timerConfig?.enabled ? timerConfig.duration : undefined
    );

    React.useEffect(() => {
        if (answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)) {
            setAnswers({ ...answerProp });
        }
    }, [answerProp]);

    if (!data) {
        return (
            <div className={cn('quiz-player quiz-player--empty text-muted-foreground italic p-4', className)}>
                {t('noQuizData', 'No quiz data')}
            </div>
        );
    }

    const allItems = React.useMemo(() => {
        if (data.sections && data.sections.length > 0) {
            return data.sections.flatMap((s) => s.items);
        }
        return data.items || [];
    }, [data]);

    const gradeableItems = allItems.filter(
        (item) => item.gradeable !== false && item.type === 'mcq'
    );

    const submittedOrFeedback = Boolean(showFeedbackProp || isReview);

    React.useEffect(() => {
        if (!timerConfig?.enabled || submittedOrFeedback) return;

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === undefined) return undefined;
                if (prev <= 1) {
                    if (timerConfig.autoSubmit) {
                        playerActions?.requestSubmit();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timerConfig?.enabled, timerConfig?.autoSubmit, submittedOrFeedback, playerActions]);

    const handleItemAnswer = (itemId: string, itemAnswer: unknown) => {
        if (disabled) return;
        const next = { ...answers, [itemId]: itemAnswer };
        setAnswers(next);
        onAnswer?.(next);
    };

    const itemResults = (resultProp?.details?.items as QuizItemResult[] | undefined) ?? [];

    const renderItem = (item: QuizItem, index: number) => {
        const answer = answers[item.id];
        const itemResult = itemResults.find((r) => r.itemId === item.id);

        switch (item.type) {
            case 'mcq':
                return (
                    <MCQPlayer
                        data={item.data as MCQElementData}
                        answer={answer as MCQAnswer}
                        onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                        answerChoicesPresentation={
                            layoutConfig?.answerChoicesPresentation ?? 'standard'
                        }
                        disabled={disabled}
                        showFeedback={Boolean(showFeedbackProp)}
                        result={itemResult}
                        isReview={isReview}
                        config={config as Record<string, unknown> | undefined}
                    />
                );
            case 'text':
                return <TextPlayer data={item.data as TextElementData} />;
            case 'image':
                return <ImagePlayer data={item.data as ImageElementData} />;
            case 'rich-text':
                return <RichTextPlayer data={item.data as RichTextElementData} />;
            default:
                return (
                    <div className="text-muted-foreground">Unknown element type: {item.type}</div>
                );
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const answeredCount = gradeableItems.filter((item) => answers[item.id] !== undefined).length;

    if (allItems.length === 0) {
        return (
            <div className={cn('quiz-player quiz-player--empty text-muted-foreground italic p-4', className)}>
                {t('noQuizItems', 'No quiz items')}
            </div>
        );
    }

    return (
        <div className={cn('quiz-player', className)}>
            <div className="quiz-player-header mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        {data.description && (
                            <p className="text-muted-foreground mt-1">{data.description}</p>
                        )}
                    </div>
                    {timerConfig?.enabled && timeRemaining !== undefined && !submittedOrFeedback && (
                        <div
                            className={cn(
                                'quiz-timer flex items-center gap-2 px-4 py-2 rounded-lg',
                                timeRemaining <= (timerConfig.warningAt || 300)
                                    ? 'bg-destructive/10 text-destructive'
                                    : 'bg-muted'
                            )}
                        >
                            <Clock className="h-5 w-5" />
                            <span className="font-mono text-lg font-bold">
                                {formatTime(timeRemaining)}
                            </span>
                        </div>
                    )}
                </div>

                {data.instructions && !submittedOrFeedback && (
                    <Card className="quiz-player-instructions mt-4">
                        <CardContent className="py-4">
                            <p className="text-sm">{data.instructions}</p>
                        </CardContent>
                    </Card>
                )}

                {layoutConfig?.showProgress && !submittedOrFeedback && gradeableItems.length > 0 && (
                    <div className="quiz-player-progress mt-4 space-y-2">
                        <div className="flex justify-between text-sm text-muted-foreground">
                            <span>{t('progress', 'Progress')}</span>
                            <span>
                                {answeredCount} / {gradeableItems.length} {t('answered', 'answered')}
                            </span>
                        </div>
                        <Progress value={(answeredCount / gradeableItems.length) * 100} />
                    </div>
                )}
            </div>

            <div className="quiz-player-content space-y-6">
                {data.sections && data.sections.length > 0
                    ? (() => {
                          let runningIndex = 0;
                          return data.sections.map((section) => (
                              <div key={section.id} className="quiz-player-section">
                                  {layoutConfig?.showSectionHeaders && section.title && (
                                      <div className="mb-4">
                                          <h3 className="text-lg font-semibold">{section.title}</h3>
                                          {section.description && (
                                              <p className="text-sm text-muted-foreground">
                                                  {section.description}
                                              </p>
                                          )}
                                      </div>
                                  )}
                                  <div className="space-y-4">
                                      {section.items.map((item) => {
                                          const currentIndex = runningIndex++;
                                          const showCounter = allItems.length > 1 && item.type === 'mcq';
                                          return (
                                              <Card key={item.id}>
                                                  <CardContent className="pt-6">
                                                      <div className={cn('quiz-player-item-inner', showCounter && 'flex gap-3 items-start')}>
                                                          {showCounter && <QuestionCounterLabel index={currentIndex} className="mt-1" />}
                                                          <div className="min-w-0 flex-1">
                                                              {renderItem(item, currentIndex)}
                                                          </div>
                                                      </div>
                                                  </CardContent>
                                              </Card>
                                          );
                                      })}
                                  </div>
                              </div>
                          ));
                      })()
                    : allItems.map((item, index) => {
                          const showCounter = allItems.length > 1 && item.type === 'mcq';
                          return (
                              <Card key={item.id}>
                                  <CardContent className="pt-6">
                                      <div className={cn('quiz-player-item-inner', showCounter && 'flex gap-3 items-start')}>
                                          {showCounter && <QuestionCounterLabel index={index} className="mt-1" />}
                                          <div className="min-w-0 flex-1">
                                              {renderItem(item, index)}
                                          </div>
                                      </div>
                                  </CardContent>
                              </Card>
                          );
                      })}
            </div>

            {!submittedOrFeedback && gradeableItems.length > 0 ? (
                <p className="text-sm text-muted-foreground text-center pt-4 quiz-player-submit-hint">
                    {answeredCount < gradeableItems.length
                        ? t('answerAllQuestions', 'Answer all questions to submit')
                        : null}
                </p>
            ) : null}
        </div>
    );
};

export default QuizPlayer;
